import { useCallback, useRef, useState } from 'react';
import { createSHA256 } from 'hash-wasm';

const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024;
const MIN_CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_MULTIPART_UPLOAD_PARTS = 10_000;
const CHUNK_SIZE_ROUNDING = 1024 * 1024;
const CHECKSUM_CHUNK_SIZE = 50 * 1024 * 1024;
const MAX_CONCURRENT_UPLOADS = 2;
const PRESIGNED_URL_WINDOW_SIZE = 10;
const MAX_RETRIES = 3;
const PART_UPLOAD_TIMEOUT_MS = 90_000;
const RETRY_BASE_DELAY_MS = 1_000;
const RETRY_MAX_DELAY_MS = 8_000;

export interface MultipartUploadProgress {
  phase: 'idle' | 'initiating' | 'uploading' | 'completing' | 'complete' | 'error' | 'aborted';
  totalParts: number;
  uploadedParts: number;
  bytesUploaded: number;
  totalBytes: number;
  percentComplete: number;
  currentPartProgress: number;
  error?: string;
}

export interface MultipartTransport<THandle> {
  initiate(input: { filename: string; fileSize: number; totalParts: number; checksum: string }): Promise<THandle>;
  getPartUrls(handle: THandle, partNumbers: number[]): Promise<Array<{ partNumber: number; url: string }>>;
  complete(handle: THandle, input: { parts: Array<{ partNumber: number; etag: string }>; checksum: string; fileSize: number }): Promise<void>;
  abort(handle: THandle): Promise<void>;
}

interface ActiveTransfer {
  transport: MultipartTransport<unknown>;
  handle: unknown;
}

const initialProgress = (): MultipartUploadProgress => ({
  phase: 'idle',
  totalParts: 0,
  uploadedParts: 0,
  bytesUploaded: 0,
  totalBytes: 0,
  percentComplete: 0,
  currentPartProgress: 0,
});

export function useMultipartTransfer() {
  const [progress, setProgress] = useState<MultipartUploadProgress>(initialProgress);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTransferRef = useRef<ActiveTransfer | null>(null);

  const updateProgress = useCallback((update: Partial<MultipartUploadProgress>) => {
    setProgress((previous) => {
      const next = { ...previous, ...update };
      if (next.totalBytes > 0) {
        next.percentComplete = Math.round((next.bytesUploaded / next.totalBytes) * 100);
      }
      return next;
    });
  }, []);

  const cleanupRemoteUpload = useCallback(async () => {
    const activeTransfer = activeTransferRef.current;
    activeTransferRef.current = null;
    if (!activeTransfer) return;
    try {
      await activeTransfer.transport.abort(activeTransfer.handle);
    } catch {
      return;
    }
  }, []);

  const upload = useCallback(async <THandle,>(
    file: File,
    transport: MultipartTransport<THandle>,
    chunkSize: number = DEFAULT_CHUNK_SIZE,
  ): Promise<boolean> => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;
    const effectiveChunkSize = calculateChunkSize(file.size, chunkSize);
    const totalParts = Math.ceil(file.size / effectiveChunkSize);

    updateProgress({
      phase: 'initiating',
      totalParts,
      uploadedParts: 0,
      bytesUploaded: 0,
      totalBytes: file.size,
      percentComplete: 0,
      currentPartProgress: 0,
      error: undefined,
    });

    try {
      const checksum = await computeChecksum(file, signal);
      const handle = await transport.initiate({
        filename: file.name,
        fileSize: file.size,
        totalParts,
        checksum,
      });
      activeTransferRef.current = { transport: transport as MultipartTransport<unknown>, handle };
      updateProgress({ phase: 'uploading' });

      const completedParts: Array<{ partNumber: number; etag: string }> = [];
      const activePartBytes = new Map<number, number>();
      let completedBytes = 0;
      const partNumbers = Array.from({ length: totalParts }, (_, index) => index + 1);
      const reportProgress = () => {
        const activeBytes = [...activePartBytes.values()].reduce((total, bytes) => total + bytes, 0);
        updateProgress({
          uploadedParts: completedParts.length,
          bytesUploaded: Math.min(file.size, completedBytes + activeBytes),
          currentPartProgress: activeBytes,
        });
      };

      for (let index = 0; index < partNumbers.length; index += PRESIGNED_URL_WINDOW_SIZE) {
        throwIfAborted(signal);
        const partWindow = partNumbers.slice(index, index + PRESIGNED_URL_WINDOW_SIZE);
        const urls = await transport.getPartUrls(handle, partWindow);

        for (let batchIndex = 0; batchIndex < partWindow.length; batchIndex += MAX_CONCURRENT_UPLOADS) {
          const batch = partWindow.slice(batchIndex, batchIndex + MAX_CONCURRENT_UPLOADS);
          await Promise.all(batch.map(async (partNumber) => {
            const start = (partNumber - 1) * effectiveChunkSize;
            const end = Math.min(start + effectiveChunkSize, file.size);
            const chunk = file.slice(start, end);
            const url = urls.find((candidate) => candidate.partNumber === partNumber)?.url;
            if (!url) throw new Error(`No URL for part ${partNumber}`);

            activePartBytes.set(partNumber, 0);
            reportProgress();
            const etag = await uploadPart(url, chunk, partNumber, signal, (bytesUploaded) => {
              activePartBytes.set(partNumber, bytesUploaded);
              reportProgress();
            });
            activePartBytes.delete(partNumber);
            completedParts.push({ partNumber, etag });
            completedBytes += end - start;
            reportProgress();
          }));
        }
      }

      updateProgress({ phase: 'completing' });
      await transport.complete(handle, { parts: completedParts, checksum, fileSize: file.size });

      updateProgress({ phase: 'complete', percentComplete: 100, bytesUploaded: file.size });
      activeTransferRef.current = null;
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
      return true;
    } catch (error) {
      const wasAborted = signal.aborted || (error instanceof Error && error.name === 'AbortError');
      if (!wasAborted) controller.abort();
      updateProgress({
        phase: wasAborted ? 'aborted' : 'error',
        error: wasAborted ? 'Upload cancelled' : error instanceof Error ? error.message : 'Upload failed',
      });
      await cleanupRemoteUpload();
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
      return false;
    }
  }, [cleanupRemoteUpload, updateProgress]);

  const abort = useCallback(async () => {
    abortControllerRef.current?.abort();
    await cleanupRemoteUpload();
    updateProgress({ phase: 'aborted', error: 'Upload cancelled by user' });
  }, [cleanupRemoteUpload, updateProgress]);

  const reset = useCallback(() => {
    setProgress(initialProgress());
    activeTransferRef.current = null;
  }, []);

  return {
    upload,
    abort,
    reset,
    progress,
    isUploading: ['initiating', 'uploading', 'completing'].includes(progress.phase),
    isComplete: progress.phase === 'complete',
    isError: progress.phase === 'error',
    isAborted: progress.phase === 'aborted',
  };
}

function calculateChunkSize(fileSize: number, preferredChunkSize: number): number {
  const minimumChunkSize = Math.ceil(fileSize / MAX_MULTIPART_UPLOAD_PARTS);
  const effectiveChunkSize = Math.max(MIN_CHUNK_SIZE, preferredChunkSize, minimumChunkSize);
  return Math.ceil(effectiveChunkSize / CHUNK_SIZE_ROUNDING) * CHUNK_SIZE_ROUNDING;
}

async function computeChecksum(file: File, signal: AbortSignal): Promise<string> {
  const sha256 = await createSHA256();
  sha256.init();
  for (let offset = 0; offset < file.size; offset += CHECKSUM_CHUNK_SIZE) {
    throwIfAborted(signal);
    const buffer = await file.slice(offset, Math.min(offset + CHECKSUM_CHUNK_SIZE, file.size)).arrayBuffer();
    sha256.update(new Uint8Array(buffer));
  }
  return sha256.digest();
}

async function uploadPart(
  url: string,
  chunk: Blob,
  partNumber: number,
  signal: AbortSignal,
  onProgress: (bytesUploaded: number) => void
): Promise<string> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    throwIfAborted(signal);
    onProgress(0);
    try {
      return await uploadPartOnce(url, chunk, partNumber, signal, onProgress);
    } catch (error) {
      if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) throw error;
      lastError = error instanceof Error ? error : new Error('Part upload failed');
      if (attempt < MAX_RETRIES - 1) await waitForRetry(attempt, signal);
    }
  }
  throw lastError || new Error(`Part ${partNumber} failed after ${MAX_RETRIES} attempts`);
}

function uploadPartOnce(
  url: string,
  chunk: Blob,
  partNumber: number,
  signal: AbortSignal,
  onProgress: (bytesUploaded: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const xhr = new XMLHttpRequest();
    let settled = false;
    const cleanup = () => {
      signal.removeEventListener('abort', handleAbort);
      xhr.upload.removeEventListener('progress', handleProgress);
    };
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };
    const handleAbort = () => {
      xhr.abort();
      finish(() => reject(createAbortError()));
    };
    const handleProgress = (event: ProgressEvent<EventTarget>) => {
      if (event.lengthComputable) onProgress(Math.min(event.loaded, chunk.size));
    };

    xhr.upload.addEventListener('progress', handleProgress);
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.timeout = PART_UPLOAD_TIMEOUT_MS;
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        finish(() => reject(createPartError(xhr, partNumber)));
        return;
      }
      const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '');
      finish(() => etag ? resolve(etag) : reject(new Error(`Part ${partNumber} completed without an ETag`)));
    };
    xhr.onerror = () => finish(() => reject(createPartError(xhr, partNumber)));
    xhr.ontimeout = () => finish(() => reject(new Error(`Part ${partNumber} timed out after ${PART_UPLOAD_TIMEOUT_MS / 1000} seconds`)));
    xhr.onabort = () => finish(() => reject(createAbortError()));
    signal.addEventListener('abort', handleAbort, { once: true });
    xhr.send(chunk);
  });
}

function waitForRetry(attempt: number, signal: AbortSignal): Promise<void> {
  const exponentialDelay = Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, RETRY_MAX_DELAY_MS);
  const delay = Math.round(exponentialDelay * (0.5 + Math.random()));
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(createAbortError());
      return;
    }
    const handleAbort = () => {
      clearTimeout(timeout);
      reject(createAbortError());
    };
    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, delay);
    signal.addEventListener('abort', handleAbort, { once: true });
  });
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw createAbortError();
}

function createAbortError(): Error {
  const error = new Error('Upload aborted');
  error.name = 'AbortError';
  return error;
}

function createPartError(xhr: XMLHttpRequest, partNumber: number): Error {
  const responseText = xhr.responseText || '';
  const errorCode = responseText.match(/<Code>([^<]+)<\/Code>/)?.[1];
  const requestId = xhr.getResponseHeader('x-amz-request-id') || responseText.match(/<RequestId>([^<]+)<\/RequestId>/)?.[1];
  let message = xhr.status
    ? `Part ${partNumber} failed with status ${xhr.status}`
    : `Part ${partNumber} failed due to a network error`;
  if (errorCode) message += ` (${errorCode})`;
  if (requestId) message += `. Request ID: ${requestId}`;
  return new Error(message);
}
